import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import Combobox from "../../../shared/ui/Field/Combobox";
import Field from "../../../shared/ui/Field/Field";

import { searchCompanies } from "../api/companies";

// CompanyFinder — feature widget. Owns the async search state for a
// single-select company picker and navigates to the safe company
// profile on selection. No Redux slice — see
// migration/phase10-composite-input-plan.md §10.

const DEBOUNCE_MS = 200;

const messageFromError = (err) => {
  if (!err) return "Помилка з'єднання.";
  if (err.response && err.response.data) {
    const data = err.response.data;
    if (typeof data === "string") return data;
    if (typeof data.detail === "string") return data.detail;
  }
  if (err.message && typeof err.message === "string") return err.message;
  return "Помилка з'єднання.";
};

const toOption = (company) => ({
  id: company.id,
  label: company.name,
  sublabel: company.city || undefined,
  raw: company,
});

function CompanyFinder() {
  const navigate = useNavigate();

  const [query, setQuery] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const latestRequestIdRef = useRef(0);
  const debounceTimerRef = useRef(null);

  // Fire a single search after the debounce window. Latest-request-id
  // guards against stale resolutions overwriting the current state.
  useEffect(() => {
    const trimmed = query.trim();

    // Empty query → cancel pending fetch, clear options, no spinner.
    if (trimmed.length === 0) {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      latestRequestIdRef.current += 1; // invalidate in-flight responses
      setOptions([]);
      setLoading(false);
      setError(null);
      return undefined;
    }

    setLoading(true);
    setError(null);

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    const requestId = latestRequestIdRef.current + 1;
    latestRequestIdRef.current = requestId;

    debounceTimerRef.current = setTimeout(() => {
      debounceTimerRef.current = null;
      searchCompanies(trimmed)
        .then((data) => {
          if (latestRequestIdRef.current !== requestId) return;
          const results = Array.isArray(data?.results) ? data.results : [];
          setOptions(results.map(toOption));
          setLoading(false);
        })
        .catch((err) => {
          if (latestRequestIdRef.current !== requestId) return;
          setError(messageFromError(err));
          setOptions([]);
          setLoading(false);
        });
    }, DEBOUNCE_MS);

    return () => {
      // Cleanup on next effect run or unmount: cancel pending debounce.
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    };
  }, [query]);

  // Final unmount cleanup — invalidate in-flight responses.
  useEffect(() => {
    return () => {
      latestRequestIdRef.current += 1;
    };
  }, []);

  const handleChange = useCallback((event) => {
    setQuery(event.target.value);
  }, []);

  const handleSelect = useCallback(
    (option) => {
      if (!option || option.id == null) return;
      navigate(`/companies/${option.id}`);
    },
    [navigate],
  );

  return (
    <div className="feature-company-finder__search">
      <Field
        id="company-finder-query"
        label="Пошук компанії"
        help="Знайдіть компанію за назвою, описом або містом."
      >
        <Combobox
          name="search"
          value={query}
          onChange={handleChange}
          options={options}
          loading={loading}
          error={error}
          emptyHint="Введіть назву компанії для пошуку."
          emptyResultsLabel={`Жодних компаній за запитом «${query.trim()}» не знайдено.`}
          onSelect={handleSelect}
          placeholder="Назва, опис або місто…"
          autoComplete="off"
        />
      </Field>
    </div>
  );
}

export default CompanyFinder;
